<script setup>
import { useI18n } from 'vue-i18n';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import RealEstateFinanceTransferBeneficiaryDetails from './RealEstateFinanceTransferBeneficiaryDetails.vue';

defineProps({
  beneficiary: { type: Object, default: null },
  amount: { type: String, default: '' },
  transferredOn: { type: String, default: '' },
  observation: { type: String, default: '' },
  receipt: { type: Object, default: null },
  saving: { type: Boolean, default: false },
  progress: { type: Number, default: 0 },
  canSubmit: { type: Boolean, default: false },
});

const emit = defineEmits([
  'update:amount',
  'update:transferredOn',
  'update:observation',
  'update:receipt',
  'submit',
]);
const { t } = useI18n();
const handleReceipt = event =>
  emit('update:receipt', event.target.files?.[0] || null);
</script>

<template>
  <LaisCard :title="t('REAL_ESTATE_FINANCE.TRANSFER.TITLE')">
    <div v-if="beneficiary" class="space-y-5">
      <RealEstateFinanceTransferBeneficiaryDetails :beneficiary="beneficiary" />
      <div class="grid gap-4 sm:grid-cols-2">
        <label class="text-sm font-medium text-lais-text">
          {{ t('REAL_ESTATE_FINANCE.TRANSFER.AMOUNT') }}
          <input
            :value="amount"
            class="mt-1 h-10 w-full rounded-lais border border-lais-border bg-white px-3 text-sm"
            inputmode="decimal"
            min="0.01"
            step="0.01"
            type="number"
            @input="emit('update:amount', $event.target.value)"
          />
        </label>
        <label class="text-sm font-medium text-lais-text">
          {{ t('REAL_ESTATE_FINANCE.TRANSFER.DATE') }}
          <input
            :value="transferredOn"
            class="mt-1 h-10 w-full rounded-lais border border-lais-border bg-white px-3 text-sm"
            type="date"
            @input="emit('update:transferredOn', $event.target.value)"
          />
        </label>
      </div>
      <label class="block text-sm font-medium text-lais-text">
        {{ t('REAL_ESTATE_FINANCE.TRANSFER.RECEIPT') }}
        <input
          accept="application/pdf,image/jpeg,image/png,.doc,.docx"
          class="mt-2 block w-full text-sm"
          type="file"
          @change="handleReceipt"
        />
      </label>
      <p
        v-if="receipt"
        class="rounded-lais bg-lais-bg-soft p-3 text-sm text-lais-text"
      >
        {{ receipt.name }}
      </p>
      <label class="block text-sm font-medium text-lais-text">
        {{ t('REAL_ESTATE_FINANCE.TRANSFER.OBSERVATION_FOR_MANAGER') }}
        <span class="font-normal text-lais-text-muted">
          {{ t('REAL_ESTATE_FINANCE.TRANSFER.OPTIONAL') }}
        </span>
        <textarea
          :value="observation"
          :placeholder="
            t('REAL_ESTATE_FINANCE.TRANSFER.OBSERVATION_PLACEHOLDER')
          "
          class="mt-1 min-h-24 w-full rounded-lais border border-lais-border bg-white p-3 text-sm"
          @input="emit('update:observation', $event.target.value)"
        />
      </label>
      <progress
        v-if="saving"
        class="h-2 w-full overflow-hidden rounded-full accent-lais-primary"
        :value="progress"
        max="100"
      />
      <LaisPrimaryButton
        icon="i-lucide-badge-check"
        :disabled="!canSubmit"
        :is-loading="saving"
        @click="emit('submit')"
      >
        {{ t('REAL_ESTATE_FINANCE.TRANSFER.CONFIRM') }}
      </LaisPrimaryButton>
    </div>
  </LaisCard>
</template>
